import {
  apiContainer,
  queryBuilder,
  massageSingleRecordBeforeUpsert
} from './apiContainer'

import { ArtificialDetailPropertyNames } from '../../common/globalNotary'
import uuid from 'react-uuid'

const SLICE_API_ROOT = '/assets'

export const buildBuildingListQuery = (args, buildingTypeId) =>
  queryBuilder(SLICE_API_ROOT, {
    ...(args || {}),
    type_id: buildingTypeId
  })

const assetApi = apiContainer.injectEndpoints({
  endpoints: build => ({
    listAssets: build.query({
      query: args => queryBuilder(SLICE_API_ROOT, args)
    }),

    getOneAsset: build.query({
      async queryFn (_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const primaryCall = fetchWithBQ(`${SLICE_API_ROOT}/${_arg.id}`)
        const entityCall = fetchWithBQ(`/entities`)
        const typeCall = fetchWithBQ(`${SLICE_API_ROOT}/~/types`)
        const containeeCall = fetchWithBQ(
          `${SLICE_API_ROOT}?parent_id=${_arg.id}`
        )
        const contractCall = fetchWithBQ(
          `/contracts?asset_id=${_arg.id}`
        )

        const res = await Promise.all([
          primaryCall,
          typeCall,
          entityCall,
          containeeCall,
          contractCall
        ])
        const primaryData = res[0]?.data?.data ?? []
        const assetTypes = res[1]?.data?.data ?? []
        const entities = res[2]?.data?.data ?? []
        const containees = res[3]?.data?.data ?? []
        const contracts = res[4]?.data?.data ?? []

        const buildingDbType = assetTypes.find(t => {
          return t.base_type === 'building'
        })

        const multiDbType = assetTypes.find(t => {
          return t.base_type === 'multi'
        })

        primaryData.isBuilding = buildingDbType.id === primaryData.type_id

        const isMulti = multiDbType.id === primaryData.type_id

        if (isMulti) {
          const buildingCall = [
            fetchWithBQ(`${SLICE_API_ROOT}/${primaryData.parent_id}`)
          ]

          const buildResults = await Promise.all(buildingCall)
          const buildingRecord = buildResults[0].data.data
          primaryData.address = buildingRecord.address
        }

        primaryData.config = buildingDbType.config
        if (
          primaryData.address &&
          primaryData.geo &&
          !primaryData.address.lat &&
          !primaryData.address.long
        ) {
          primaryData.address.lat = primaryData.geo.lat
          primaryData.address.long = primaryData.geo.long
        }

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1] =
          assetTypes
        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
        ] = assetTypes

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2] =
          entities

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
        ] = entities

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3] =
          containees

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
        ] = containees

        primaryData[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4] =
          contracts

        primaryData[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
        ] = contracts

        // tmp fix for saving auxiliary updates
        return { data: { data: { ...primaryData, _objectId: uuid() } } }
      }
    }),

    listBuildings: build.query({
      async queryFn (_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const fetchCallArray = [fetchWithBQ(`${SLICE_API_ROOT}/~/types`)]

        const firstResult = await Promise.all(fetchCallArray)
        const assetTypes = firstResult[0].data.data
        const buildingType = assetTypes.find(o => {
          return o.base_type === 'building'
        })

        const buildingTypeId = buildingType.id

        fetchCallArray.splice(0, fetchCallArray.length)

        fetchCallArray.push(
          fetchWithBQ(buildBuildingListQuery(_arg, buildingTypeId))
        )

        const secondResult = await Promise.all(fetchCallArray)

        return secondResult[0]
      }
    }),

    listNonContainers: build.query({
      query: args => queryBuilder(SLICE_API_ROOT, {
        exclude_containers: 1,
        ...args
      })
    }),

    bulkUpdateAssets: build.mutation({
      query: args => {
        return {
          url: SLICE_API_ROOT,
          method: 'PATCH',
          body: { data: args }
        }
      }
    }),
    listAssetTypes: build.query({
      query: () => `${SLICE_API_ROOT}/~/types`,
      keepUnusedDataFor: 86400
    }),
    upsertAsset: build.mutation({
      query: args => {
        const { id } = args
        const actualArgs = massageSingleRecordBeforeUpsert(args)
        const config = actualArgs.config
        delete actualArgs.config
        delete actualArgs.legacy
        delete actualArgs.isBuilding
        delete actualArgs.addresses
        delete actualArgs.__permissions__

        if (config.is_container) {
          actualArgs.entity_id = null
        }

        if (actualArgs.address && !actualArgs.address.unit) {
          actualArgs.address.unit = null
        }

        if (!config.permit_geo) {
          actualArgs.geo = null
        } else {
          if (actualArgs.geo) {
            const g = actualArgs.geo
            delete g.name
            actualArgs.geo = g
          }
        }

        if (!config.permit_address) {
          actualArgs.address = null
        }
        if (!config.allow_parents) {
          actualArgs.parent_id = null
        }

        // if (!actualArgs.parent_id) actualArgs.parent_id = null
        // if (!actualArgs.building_floor) actualArgs.building_floor = null
        // if (!actualArgs.floor_unit) actualArgs.floor_unit = null

        // if (!actualArgs.year_built) actualArgs.year_built = null

        // if (!actualArgs.strata) actualArgs.strata = null
        // if (!actualArgs.num_floors_units) actualArgs.num_floors_units = null

        if (id) {
          // update

          return {
            url: `${SLICE_API_ROOT}/${id}`,
            method: 'PUT',
            body: actualArgs
          }
        } //insert
        else {
          return {
            url: SLICE_API_ROOT,
            method: 'POST',
            body: actualArgs
          }
        }
      }
    }),
    deleteAsset: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/${args.id}`,
          method: 'DELETE'
        }
      }
    })
  }),
  overrideExisting: true
})

export const {
  useGetOneAssetQuery,
  useLazyGetOneAssetQuery,
  useLazyListAssetsQuery,
  useLazyListNonContainersQuery,
  useListAssetTypesQuery,
  useLazyListAssetTypesQuery,
  useLazyListBuildingsQuery,
  useDeleteAssetMutation,
  useUpsertAssetMutation,
  useBulkUpdateAssetsMutation
} = assetApi
